import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Badge,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
} from "@chakra-ui/react";
import { HelmetComponent } from "../../components/HelmetComponent";
import { PublisherAndUserContext } from "../../contexts/PublisherAndUserContext";
import { useQuery, NetworkStatus } from "@apollo/client";
import { MARKETING_DASHBOARD, REVENUE_DASHBOARD } from "../../graphql/queries";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { FiEye, FiUserPlus, FiShoppingCart, FiBookOpen, FiClock, FiBarChart2 } from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

// ----- Sample data similar to analytics-inspiration (marketing only) -----
const generateDates = (days) => {
  const arr = [];
  for (let i = days; i >= 1; i--) arr.push(`Day ${i}`);
  return arr;
};

const getMarketingData = (range) => {
  switch (range) {
    case "7d":
      return {
        pageViews: 12450,
        signUps: 380,
        conversionRate: 3.1,
        change: 4.2,
        signUpChange: 6.8,
        conversionChange: 0.7,
        trafficSources: [
          { name: "Organic", value: 38 },
          { name: "Paid", value: 24 },
          { name: "Referral", value: 18 },
          { name: "Social", value: 12 },
          { name: "Direct", value: 8 },
        ],
        pageViewHistory: generateDates(7).map((d, i) => ({ date: d, value: 9000 + i * 250 })),
        landingPages: [
          { name: "Web Dev", views: 4200, conversions: 140 },
          { name: "Python", views: 3800, conversions: 130 },
          { name: "Data Sci", views: 3100, conversions: 95 },
          { name: "UX Design", views: 2600, conversions: 70 },
          { name: "Marketing", views: 2100, conversions: 60 },
        ],
        keywords: [
          { keyword: "web development", searches: 1250 },
          { keyword: "python course", searches: 1120 },
          { keyword: "data science", searches: 980 },
          { keyword: "ux design", searches: 860 },
          { keyword: "digital marketing", searches: 790 },
        ],
      };
    case "30d":
      return {
        pageViews: 48200,
        signUps: 1510,
        conversionRate: 3.3,
        change: 8.1,
        signUpChange: 10.2,
        conversionChange: 1.1,
        trafficSources: [
          { name: "Organic", value: 41 },
          { name: "Paid", value: 22 },
          { name: "Referral", value: 17 },
          { name: "Social", value: 12 },
          { name: "Direct", value: 8 },
        ],
        pageViewHistory: generateDates(30).map((d, i) => ({ date: d, value: 10000 + (i % 6) * 600 })),
        landingPages: [
          { name: "Web Dev", views: 16200, conversions: 520 },
          { name: "Python", views: 14800, conversions: 480 },
          { name: "Data Sci", views: 12100, conversions: 365 },
          { name: "UX Design", views: 10200, conversions: 280 },
          { name: "Marketing", views: 8800, conversions: 245 },
        ],
        keywords: [
          { keyword: "web development", searches: 5200 },
          { keyword: "python course", searches: 4860 },
          { keyword: "data science", searches: 4200 },
          { keyword: "ux design", searches: 3910 },
          { keyword: "digital marketing", searches: 3480 },
        ],
      };
    case "6m":
    case "1y":
    case "all":
    default:
      return {
        pageViews: 180000,
        signUps: 6200,
        conversionRate: 3.4,
        change: 12.4,
        signUpChange: 14.1,
        conversionChange: 1.6,
        trafficSources: [
          { name: "Organic", value: 42 },
          { name: "Paid", value: 21 },
          { name: "Referral", value: 16 },
          { name: "Social", value: 12 },
          { name: "Direct", value: 9 },
        ],
        pageViewHistory: generateDates(12).map((d, i) => ({ date: `M${i + 1}`, value: 12000 + (i % 4) * 1500 })),
        landingPages: [
          { name: "Web Dev", views: 62000, conversions: 2150 },
          { name: "Python", views: 54000, conversions: 1980 },
          { name: "Data Sci", views: 45800, conversions: 1450 },
          { name: "UX Design", views: 39200, conversions: 1170 },
          { name: "Marketing", views: 34100, conversions: 990 },
        ],
        keywords: [
          { keyword: "web development", searches: 22200 },
          { keyword: "python course", searches: 20450 },
          { keyword: "data science", searches: 18900 },
          { keyword: "ux design", searches: 16800 },
          { keyword: "digital marketing", searches: 15050 },
        ],
      };
  }
};

const MetricCard = ({ title, value, icon, change, changeLabel = "vs prior" }) => {
  const border = useColorModeValue("gray.200", "gray.700");
  const pos = change >= 0;
  return (
    <Box p={4} borderWidth="1px" borderColor={border} borderRadius="md">
      <Flex align="center" justify="space-between" mb={2}>
        <Text fontWeight={600}>{title}</Text>
        <Box color="gray.500">{icon}</Box>
      </Flex>
      <Text fontSize="2xl" fontWeight="700">{value}</Text>
      {typeof change === "number" && (
        <Text fontSize="sm" color={pos ? "green.500" : "red.500"}>
          {pos ? "+" : ""}{change}% {changeLabel}
        </Text>
      )}
    </Box>
  );
};

// ----- Revenue (live) -----
const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);


const RevenueSection = ({ timeRange, active }) => {
  const { publisher } = useContext(PublisherAndUserContext);

  const { from, to, interval } = React.useMemo(() => {
    const startOfUTC = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
    const endOfUTC = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
    const today = new Date();
    let fromDate = null;
    let intv = 'MONTHLY';
    switch (timeRange) {
      case '7d': { const d = new Date(today); d.setUTCDate(d.getUTCDate() - 7); fromDate = startOfUTC(d); intv = 'DAILY'; break; }
      case '30d': { const d = new Date(today); d.setUTCDate(d.getUTCDate() - 30); fromDate = startOfUTC(d); intv = 'DAILY'; break; }
      case '6m': { const d = new Date(today); d.setUTCMonth(d.getUTCMonth() - 6); fromDate = startOfUTC(d); intv = 'MONTHLY'; break; }
      case '1y': { const d = new Date(today); d.setUTCFullYear(d.getUTCFullYear() - 1); fromDate = startOfUTC(d); intv = 'MONTHLY'; break; }
      case 'all':
      default: fromDate = null;
    }
    const toDate = endOfUTC(today);
    return { from: fromDate ? fromDate.toISOString() : null, to: toDate.toISOString(), interval: intv };
  }, [timeRange]);

  const { data: revData, previousData: prevRev, loading: revLoading, networkStatus: revNetwork } = useQuery(REVENUE_DASHBOARD, {
    skip: !active || !publisher?.id,
    variables: { publisherId: publisher?.id, from, to, interval, limitTop: 5 },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const rev = revData ?? prevRev;
  const isRevRefreshing = revLoading && !!prevRev;
  const showOverlay = (!!active) && revLoading && !prevRev;
  const ov = rev?.revenueOverview;
  const rSeries = rev?.revenueSeries || [];
  const pSeries = rev?.purchasesSeries || [];
  // Zero fill daily for prettier trend; monthly uses server buckets directly
  const revenueHistory = (() => {
    if (interval === 'DAILY') {
      const result = [];
      const endDate = to ? new Date(to) : new Date();
      const startDate = from ? new Date(from) : new Date(endDate.getTime() - 6 * 24 * 60 * 60 * 1000);
      startDate.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(0, 0, 0, 0);
      const map = new Map((rSeries || []).map((r) => [r.bucket, r]));
      const cursor = new Date(startDate);
      while (cursor <= endDate) {
        const key = cursor.toISOString().slice(0, 10);
        const row = map.get(key);
        result.push({ date: key, value: row ? row.revenue : 0 });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      return result;
    }
    return (rSeries || []).map((r) => ({ date: r.bucket, value: r.revenue }));
  })();
  const purchasesByPeriod = (pSeries || []).map((r) => ({ name: r.bucket, purchases: r.purchases }));
  const topCourses = (ov?.topProducts || []).map((p) => ({ name: p.productName || p.productId, revenue: p.revenue, enrollments: p.purchases }));
  return (
    <Box>
      {showOverlay && <LoadingOverlay loadingMessage="Loading revenue data..." />}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <MetricCard title="Purchases" value={(ov?.purchases || 0).toLocaleString()} icon={<FiShoppingCart size={20} />} />
        <MetricCard title="Revenue" value={formatCurrency(ov?.periodRevenue || 0)} icon={<FiShoppingCart size={20} />} />
        <MetricCard title="All-Time Revenue" value={formatCurrency(ov?.allTimeRevenue || 0)} icon={<FiShoppingCart size={20} />} />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
        <Box p={6} bg={useColorModeValue("white", "gray.800")} borderRadius="lg" boxShadow="md" position="relative">
          <Heading size="sm" mb={4}>Revenue Trend</Heading>
          {isRevRefreshing && (
            <Flex position="absolute" top="10px" right="12px" align="center">
              <Spinner size="sm" mr={2} />
              <Text fontSize="xs" color="gray.500">Refreshing</Text>
            </Flex>
          )}
          <Box height="260px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                <Tooltip formatter={(v) => [`$${v}`, "Revenue"]} />
                <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box p={6} bg={useColorModeValue("white", "gray.800")} borderRadius="lg" boxShadow="md" position="relative">
          <Heading size="sm" mb={4}>Purchases by Period</Heading>
          {isRevRefreshing && (
            <Flex position="absolute" top="10px" right="12px" align="center">
              <Spinner size="sm" mr={2} />
              <Text fontSize="xs" color="gray.500">Refreshing</Text>
            </Flex>
          )}
          <Box height="260px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={purchasesByPeriod}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                <Tooltip formatter={(v) => [v, "Purchases"]} />
                <Legend />
                <Bar dataKey="purchases" fill="#3b82f6" name="Purchases" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </SimpleGrid>

      <Box p={6} bg={useColorModeValue("white", "gray.800")} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="sm" mb={4}>Top Performing Products</Heading>
        <Box height="260px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topCourses} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={150} />
              <Tooltip formatter={(v) => [`$${v}`, "Revenue"]} />
              <Bar dataKey="revenue" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};


// ----- Marketing (live) -----
const MarketingSection = ({ timeRange, active }) => {
  const { publisher } = useContext(PublisherAndUserContext);
  const [campaign, setCampaign] = useState("");

  const { from, to, interval } = React.useMemo(() => {
    const startOfUTC = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
    const endOfUTC = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
    const today = new Date();
    let fromDate = null;
    let intv = (timeRange === '30d' || timeRange === '7d') ? 'DAILY' : 'WEEKLY';
    switch (timeRange) {
      case '7d': { const d = new Date(today); d.setUTCDate(d.getUTCDate() - 7); fromDate = startOfUTC(d); break; }
      case '30d': { const d = new Date(today); d.setUTCDate(d.getUTCDate() - 30); fromDate = startOfUTC(d); break; }
      case '6m': { const d = new Date(today); d.setUTCMonth(d.getUTCMonth() - 6); fromDate = startOfUTC(d); break; }
      case '1y': { const d = new Date(today); d.setUTCFullYear(d.getUTCFullYear() - 1); fromDate = startOfUTC(d); break; }
      case 'all':
      default: fromDate = null;
    }
    const toDate = endOfUTC(today);
    return { from: fromDate ? fromDate.toISOString() : null, to: toDate.toISOString(), interval: intv };
  }, [timeRange]);

  const { data: dashData, previousData: prevDash, loading: mktLoading, networkStatus: mktNetwork } = useQuery(MARKETING_DASHBOARD, {
    skip: !active || !publisher?.id,
    variables: { publisherId: publisher?.id, from, to, interval, limit: 10 },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const data = dashData ?? prevDash;
  const isMktRefreshing = mktLoading && !!prevDash;
  const showOverlay = (!!active) && mktLoading && !prevDash;
  const ov = data?.marketingOverview;
  const ts = data?.timeSeries || [];
  const kws = data?.topKeywords || [];
  const trafficSources = (ov?.topReferrers || []).map((r) => ({ name: r.source, value: r.count }));
  // Zero-fill only for DAILY interval; for WEEKLY use server buckets directly
  const pageViewHistory = (() => {
    if (interval === 'DAILY') {
      const result = [];
      const endDate = to ? new Date(to) : new Date();
      const startDate = from ? new Date(from) : new Date(endDate.getTime() - 6 * 24 * 60 * 60 * 1000);
      startDate.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(0, 0, 0, 0);
      const map = new Map((ts || []).map((r) => [r.bucket, r]));
      const cursor = new Date(startDate);
      while (cursor <= endDate) {
        const key = cursor.toISOString().slice(0, 10); // YYYY-MM-DD
        const row = map.get(key);
        result.push({ date: key, value: row ? row.views : 0, visits: row ? row.visits : 0, buys: row ? row.buys : 0 });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      return result;
    }
    // WEEKLY or other: trust server buckets (already aggregated at week start)
    return (ts || []).map((r) => ({ date: r.bucket, value: r.views }));
  })();
  const landingData = (ov?.funnelByProduct || []).map((f) => ({ name: f.productName || f.productId, Views: f.views, Conversions: f.buys, Remainder: Math.max(0, f.views - f.buys) }));
  const keywords = kws.map((k) => ({ keyword: k.keyword, searches: k.count }));
  const pieColors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  return (
    <Box>
      {showOverlay && <LoadingOverlay loadingMessage="Loading marketing data..." />}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
        <MetricCard title="Visits" value={(ov?.visits || 0).toLocaleString()} icon={<FiUserPlus size={20} />} />
        <MetricCard title="Page Views" value={(ov?.views || 0).toLocaleString()} icon={<FiEye size={20} />} />
        <MetricCard title="Buy Clicks" value={(ov?.buys || 0).toLocaleString()} icon={<FiShoppingCart size={20} />} />
        <MetricCard title="Buy CTR" value={`${((ov?.buyCTR || 0) * 100).toFixed(2)}%`} icon={<FiBarChart2 size={20} />} />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
        <Box p={6} bg={useColorModeValue("white", "gray.800")} borderRadius="lg" boxShadow="md" position="relative">
          <Heading size="sm" mb={4}>Traffic Sources</Heading>
          {isMktRefreshing && (
            <Flex position="absolute" top="10px" right="12px" align="center">
              <Spinner size="sm" mr={2} />
              <Text fontSize="xs" color="gray.500">Refreshing</Text>
            </Flex>
          )}
          <Box height="260px">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={trafficSources} dataKey="value" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                  {trafficSources.map((_, i) => (
                    <Cell key={i} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [`${v}`, "Count"]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box p={6} bg={useColorModeValue("white", "gray.800")} borderRadius="lg" boxShadow="md" position="relative">
          <Heading size="sm" mb={4}>Page Views Trend</Heading>
          {isMktRefreshing && (
            <Flex position="absolute" top="10px" right="12px" align="center">
              <Spinner size="sm" mr={2} />
              <Text fontSize="xs" color="gray.500">Refreshing</Text>
            </Flex>
          )}
          <Box height="260px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={pageViewHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                <Tooltip formatter={(v) => [v.toLocaleString(), "Views"]} />
                <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
        <Box p={6} bg={useColorModeValue("white", "gray.800")} borderRadius="lg" boxShadow="md" position="relative">
          <Heading size="sm" mb={4}>Product Funnel (Views vs Buy Clicks)</Heading>
          {isMktRefreshing && (
            <Flex position="absolute" top="10px" right="12px" align="center">
              <Spinner size="sm" mr={2} />
              <Text fontSize="xs" color="gray.500">Refreshing</Text>
            </Flex>
          )}
          <Box height="260px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={landingData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                <Tooltip formatter={(value, name) => {
                  if (name === "Conversions") return [`${value.toLocaleString()} buys`, name];
                  if (name === "Views" || name === "Remainder") return [`${value.toLocaleString()} views`, "Views"];
                  return [value, name];
                }} />
                <Legend />
                <Bar dataKey="Conversions" stackId="a" fill="#10b981" name="Buys" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Remainder" stackId="a" fill="#3b82f6" name="Views" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box p={6} bg={useColorModeValue("white", "gray.800")} borderRadius="lg" boxShadow="md" position="relative">
          <Heading size="sm" mb={4}>Top Keywords</Heading>
          {isMktRefreshing && (
            <Flex position="absolute" top="10px" right="12px" align="center">
              <Spinner size="sm" mr={2} />
              <Text fontSize="xs" color="gray.500">Refreshing</Text>
            </Flex>
          )}
          <Box height="260px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={keywords} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="keyword" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [v.toLocaleString(), "Count"]} />
                <Bar dataKey="searches" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export const AnalyticsPage = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const { user } = useContext(PublisherAndUserContext);
  const [range, setRange] = useState("7d");

  return (
    <>
      <HelmetComponent title={`Analytics - Home - ${user?.firstName} ${user?.lastName}`} />

      <Box>
        <Flex align="center" justify="space-between" mb={4}>
          <Flex align="center">
            <Heading as="h2" size="md">Analytics</Heading>
            <Badge colorScheme="purple" ml={3}>Preview</Badge>
          </Flex>
          <Flex align="center">
            <Select size="sm" width="160px" value={range} onChange={(e) => setRange(e.target.value)}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="6m">Last 6 months</option>
              <option value="1y">Last 1 year</option>
              <option value="all">All time</option>
            </Select>
          </Flex>
        </Flex>

        <Tabs variant="enclosed" colorScheme="blue" index={tabIndex} onChange={setTabIndex} isLazy>
          <TabList>
            <Tab>Sales Performance</Tab>
            <Tab>Traffic & Reach</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <RevenueSection timeRange={range} active={tabIndex === 0} />
            </TabPanel>
            <TabPanel>
              <MarketingSection timeRange={range} active={tabIndex === 1} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </>
  );
};



